import { createAsyncThunk } from '@reduxjs/toolkit';
import apiFetch, { genericErrorMessage } from '@/services/apiFetch.ts';
import {
  getApplicantDataset,
  getApplicantSettings,
  setApplicantSettings,
} from '@/services/applicants.endpoint.ts';
import {
  ApplicantSettingsFilterType,
  ApplicantSettingsType,
  ApplicantType,
} from 'interfaces/applicants.interface.ts';
import { RootState } from '@/store';
import { Nullable } from 'interfaces/Nullable.ts';

export const fetchSelectedApplicantSettingsPWA = createAsyncThunk(
  'FETCH_SELECTED_APPLICANT_SETTINGS_PWA',
  async (_, thunkAPI) => {
    try {
      const store = thunkAPI.getState() as RootState;
      const pvAccountID = store.accountsInfo.account?.id;
      const incidentID = store.incidents.selectedIncidentForParcelSyncID;
      const { selectedGroup, selectedGroupID } = store.accountsInfo;
      const activeGroup = selectedGroup ? selectedGroup : selectedGroupID;
      if (!pvAccountID) return Promise.reject('No User Account ID Found');
      if (!incidentID) return Promise.reject('No Incident ID Found');
      const pvSettingType = `selectedApplicantSettingsPWA-${incidentID}`;
      const endpoint = getApplicantSettings({ pvSettingType, pvGroupID: Number(activeGroup) });
      return apiFetch<ApplicantSettingsType[]>(endpoint).then((res) => res.data ?? null);
    } catch (error) {
      let message = genericErrorMessage;
      if (error instanceof Error) message = error.message;
      else {
        if (typeof error === 'object' && error !== null) {
          if ('message' in error) {
            message = String(error.message);
          }
        }
      }
      throw Error(message);
    }
  }
);
export const fetchApplicants = createAsyncThunk('FETCH_APPLICANTS', async (_, thunkAPI) => {
  try {
    const store = thunkAPI.getState() as RootState;
    const selectedApplicant = store.applicants.selectedApplicant;
    const endpoint = getApplicantDataset();
    const response = {
      applicants: { count: 0, dataset: [] } as { count: number; dataset: ApplicantType[] },
      selectedApplicant: {} as Nullable<ApplicantType>,
    };
    const applicants = await apiFetch<{ count: number; dataset: ApplicantType[] }>(endpoint).then(
      (res) => res.data
    );
    //if there is no selected applicant, search the applicants for the county / group name that is set in portal
    if (!selectedApplicant) {
      const selectedGroupNameForParcels = store.accountsInfo.selectedGroupNameForParcels;
      if (selectedGroupNameForParcels) {
        const selectedApplicant = applicants.dataset.find((applicant) =>
          applicant.applicant_name.toLowerCase().includes(selectedGroupNameForParcels.toLowerCase())
        );
        if (selectedApplicant && Object.keys(selectedApplicant).length) {
          response.selectedApplicant = selectedApplicant;
        }
      }
    }
    response.applicants = applicants;
    return response;
  } catch (error) {
    let message = genericErrorMessage;
    if (error instanceof Error) message = error.message;
    else {
      if (typeof error === 'object' && error !== null) {
        if ('message' in error) {
          message = String(error.message);
        }
      }
    }
    throw Error(message);
  }
});

export const updateSelectedApplicantSettingsPWA = createAsyncThunk(
  'UPDATE_SELECTED_APPLICANT_SETTINGS_PWA',
  async (settings: ApplicantType, thunkAPI) => {
    try {
      const store = thunkAPI.getState() as RootState;
      const { account, selectedGroup, selectedGroupID } = store.accountsInfo;
      const pvAccountID = account?.id;
      const applicantSettingsID = store.applicants.userSelectedApplicantSettingID;
      const incidentId = store.incidents.selectedIncidentForParcelSyncID;
      const activeGroup = selectedGroup ? selectedGroup : selectedGroupID;
      if (!pvAccountID) return Promise.reject('No User Account ID Found');
      const payload: ApplicantSettingsFilterType = {
        pvSettingType: `selectedApplicantSettingsPWA-${incidentId}`,
        pvSettingValue: JSON.stringify(settings),
        pvGroupID: Number(activeGroup),
        pvAccountID,
      };
      if (applicantSettingsID) payload.pvSettingID = applicantSettingsID;
      const endpoint = setApplicantSettings(payload);
      return apiFetch<ApplicantSettingsType>(endpoint).then((res) => res.data ?? null);
    } catch (error) {
      let message = genericErrorMessage;
      if (error instanceof Error) message = error.message;
      else {
        if (typeof error === 'object' && error !== null) {
          if ('message' in error) {
            message = String(error.message);
          }
        }
      }
      throw Error(message);
    }
  }
);
